import { z } from "zod";
import { calendarEventFieldLimits } from "calendar/calendar-event-fields";

export const calendarRecurrenceFrequencies = ["daily", "weekly", "monthly", "yearly"] as const;
export const calendarWeekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

const recurrenceFrequencySchema = z.preprocess(
  (value) => (typeof value === "number" ? calendarRecurrenceFrequencies[value] : value),
  z.enum(calendarRecurrenceFrequencies),
);

const weekdaySchema = z.preprocess(
  (value) => (typeof value === "number" ? calendarWeekdays[value] : value),
  z.enum(calendarWeekdays),
);

export const CalendarEventSourceSchema = z.object({
  module: z.string(),
  resourceType: z.string(),
  resourceId: z.uuid(),
  slot: z.string(),
});

export const CalendarRecurrenceSchema = z.object({
  frequency: recurrenceFrequencySchema,
  interval: z.number().int().positive(),
  weekdays: z.array(weekdaySchema),
  count: z.number().int().positive().nullable(),
  untilDate: z.string().nullable(),
  untilUtc: z.string().nullable(),
});

export const CalendarEventResponseSchema = z.object({
  id: z.uuid(),
  createdDate: z.string().datetime({ offset: true }),
  title: z.string(),
  description: z.string().nullable(),
  link: z.string().nullable(),
  start: z.union([z.string(), z.number()]),
  end: z.union([z.string(), z.number()]).nullable(),
  isAllDay: z.boolean(),
  timeZoneId: z.string().nullable(),
  recurrence: CalendarRecurrenceSchema.nullable(),
  source: CalendarEventSourceSchema.nullable(),
  isSourceManaged: z.boolean(),
  canEdit: z.boolean(),
});

export const CalendarEventCreateRequestSchema = z.object({
  title: z.string().min(1).max(calendarEventFieldLimits.title),
  startDate: z.iso.date().optional(),
  endDate: z.iso.date().optional(),
  start: z.iso.datetime({ offset: true }).optional(),
  end: z.iso.datetime({ offset: true }).optional(),
  timeZoneId: z.string().optional(),
  description: z.string().max(calendarEventFieldLimits.description).optional(),
  link: z.string().max(calendarEventFieldLimits.link).optional(),
  recurrence: z
    .object({
      frequency: recurrenceFrequencySchema,
      interval: z.number().int().positive(),
      weekdays: z.array(weekdaySchema).optional(),
      count: z.number().int().positive().nullable().optional(),
      untilDate: z.iso.date().optional(),
      until: z.iso.datetime({ offset: true }).optional(),
    })
    .nullable()
    .optional(),
}).superRefine((request, context) => {
  const hasAllDayStart = request.startDate !== undefined;
  const hasTimedStart = request.start !== undefined;
  if (hasAllDayStart === hasTimedStart) {
    context.addIssue({
      code: "custom",
      message: "Specify exactly one of startDate or start.",
      path: [hasAllDayStart ? "start" : "startDate"],
    });
  }
  if (!hasAllDayStart && request.endDate !== undefined) {
    context.addIssue({ code: "custom", message: "endDate requires startDate.", path: ["endDate"] });
  }
  if (!hasTimedStart && request.end !== undefined) {
    context.addIssue({ code: "custom", message: "end requires start.", path: ["end"] });
  }

  const hasUntilDate = request.recurrence?.untilDate !== undefined;
  const hasTimedUntil = request.recurrence?.until !== undefined;
  if (hasUntilDate && hasTimedUntil) {
    context.addIssue({
      code: "custom",
      message: "Specify at most one of untilDate or until.",
      path: ["recurrence", "until"],
    });
  }
  if (hasAllDayStart && hasTimedUntil) {
    context.addIssue({
      code: "custom",
      message: "until requires a timed start.",
      path: ["recurrence", "until"],
    });
  }
  if (hasTimedStart && hasUntilDate) {
    context.addIssue({
      code: "custom",
      message: "untilDate requires an all-day startDate.",
      path: ["recurrence", "untilDate"],
    });
  }
});

export type CalendarEventResponse = z.infer<typeof CalendarEventResponseSchema>;
export type CalendarEventOccurrenceTime = Pick<
  CalendarEventResponse,
  "start" | "end" | "isAllDay" | "timeZoneId"
>;
export type CalendarEventSource = z.infer<typeof CalendarEventSourceSchema>;
export type CalendarRecurrence = z.infer<typeof CalendarRecurrenceSchema>;
export type CalendarEventCreateRequest = z.infer<typeof CalendarEventCreateRequestSchema>;

export type CalendarEventPatchOperation = {
  op: "add" | "remove" | "replace";
  path: string;
  value?: unknown;
};
